import { NavLink, Outlet } from "react-router-dom";

import { Icon } from "../components/Icon.js";
import { LanguageChooser } from "../components/LanguageChooser.js";
import { StatusBanner } from "../components/StatusBanner.js";
import { useOnlineStatus } from "../hooks/useOnlineStatus.js";
import { useI18n } from "../i18n/I18nProvider.js";
import { primaryNavigation, secondaryNavigation } from "./navigation.js";

export function AppShell() {
  const { locale, selectLocale, t } = useI18n();
  const isOnline = useOnlineStatus();

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-content">
        {t("skip.main")}
      </a>
      <header className="topbar">
        <div className="topbar__inner">
          <NavLink aria-label={t("nav.home")} className="brand" to="/">
            <span className="brand__mark">
              <Icon name="shield" />
            </span>
            <span>
              <strong>{t("app.name")}</strong>
              <small>{t("app.tagline")}</small>
            </span>
            <span className="pre-release-label">{t("build.stage_short")}</span>
          </NavLink>
          <div aria-label={t("language.choose")} className="quick-language-switcher" role="group">
            <button
              aria-label={t("language.th")}
              aria-pressed={locale === "th"}
              onClick={() => selectLocale("th")}
              type="button"
            >
              ไทย
            </button>
            <button
              aria-label={t("language.en")}
              aria-pressed={locale === "en"}
              onClick={() => selectLocale("en")}
              type="button"
            >
              EN
            </button>
          </div>
          <nav aria-label={t("nav.primary_desktop")} className="top-navigation">
            {primaryNavigation.map((item) => (
              <NavLink
                className={({ isActive }) =>
                  `top-nav-link${item.path === "/emergency" ? " top-nav-link--emergency" : ""}${isActive ? " top-nav-link--active" : ""}`
                }
                end={item.path === "/"}
                key={item.path}
                to={item.path}
              >
                <Icon name={item.icon} />
                {t(item.key)}
              </NavLink>
            ))}
          </nav>
          <nav aria-label={t("nav.secondary_desktop")} className="sidebar-secondary-navigation">
            {secondaryNavigation.map((item) => (
              <NavLink
                className={({ isActive }) =>
                  `sidebar-secondary-link${isActive ? " sidebar-secondary-link--active" : ""}`
                }
                key={item.path}
                to={item.path}
              >
                <Icon name={item.icon} />
                <span>{t(item.key)}</span>
              </NavLink>
            ))}
          </nav>
        </div>
      </header>
      {!isOnline && (
        <div className="connection-status">
          <StatusBanner
            body={t("status.connection_unavailable.body")}
            live
            title={t("status.connection_unavailable.title")}
            tone="notice"
          />
        </div>
      )}
      <div className="shell-layout">
        <main className="main-content" id="main-content" tabIndex={-1}>
          <Outlet />
        </main>
      </div>
      <footer className="app-footer">
        <p>
          © 2026{" "}
          <a
            className="app-footer__brand"
            href="https://craftlab.guru/"
            rel="noreferrer"
            target="_blank"
          >
            craftlab.guru
          </a>
        </p>
        <span aria-hidden="true" className="app-footer__separator">
          ❘
        </span>
        <a
          className="app-footer__support"
          href="https://ko-fi.com/craftlabguru"
          rel="noreferrer"
          target="_blank"
        >
          {t("support.footer_link")}
        </a>
      </footer>
      <nav aria-label={t("nav.primary_mobile")} className="bottom-navigation">
        {primaryNavigation.map((item) => (
          <NavLink
            className={({ isActive }) =>
              `bottom-nav-link${item.path === "/emergency" ? " bottom-nav-link--emergency" : ""}${isActive ? " bottom-nav-link--active" : ""}`
            }
            end={item.path === "/"}
            key={item.path}
            to={item.path}
          >
            <Icon name={item.icon} />
            <span>{t(item.key)}</span>
          </NavLink>
        ))}
      </nav>
      <LanguageChooser />
    </div>
  );
}
